'use es6';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { registerQuery, useQuery } from 'data-fetching-client';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import Review from 'marketplace-ui-reviews/records/Review';
import ReviewStats from 'marketplace-ui-reviews/records/ReviewStats';
import { PAGE_KEY, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { resetProfileReviewFilters, updateReviewFilters } from '../../actions/ReviewActions';
import { getReviewFilters } from '../../selectors/reviews';
import { getServices } from '../../slices/serviceCatalogSlice';
import ProfileReviewRenderer from './ProfileReviewRenderer';
import { fetchUserHasProfile } from '../../clients/profilesClient';
import { getIsPublicApp } from '../../selectors/app';
import * as CustomPropTypes from '../../constants/PropTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const GET_USER_HAS_PROFILE = registerQuery({
  fieldName: 'hasProfile',
  fetcher: fetchUserHasProfile
});
const ReviewsCardBody = ({
  onTrackInteraction,
  scrollToTabSection,
  reviews,
  reviewStats,
  reviewsFetchStatus,
  reviewsTotal,
  reviewsCount
}) => {
  const {
    inline
  } = useResponsiveProps({
    inline: {
      xs: false,
      lg: true
    }
  });
  const [searchSubmitted, setSearchSubmitted] = useState('');
  const dispatch = useDispatch();
  const reviewFilters = useSelector(getReviewFilters);
  const catalogServices = useSelector(getServices);
  const isPublicApp = useSelector(getIsPublicApp);
  useEffect(() => {
    return () => {
      dispatch(resetProfileReviewFilters());
    };
  }, [dispatch]);
  const {
    data,
    error,
    loading
  } = useQuery(GET_USER_HAS_PROFILE, {
    skip: isPublicApp
  });
  const hasProfile = useMemo(() => data ? data.hasProfile : false, [data]);
  const handleTrackInteraction = useCallback((action, extras = {}) => {
    onTrackInteraction(action, Object.assign({}, extras, {
      section: 'reviews'
    }));
  }, [onTrackInteraction]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    handleTrackInteraction(`change reviews ${textKey}`, {
      value: `${value}`
    });
    if (textKey === PAGE_KEY) {
      scrollToTabSection('reviews');
    }
    if (textKey === SEARCH_KEY) {
      setSearchSubmitted(value);
    }
    dispatch(updateReviewFilters({
      [textKey]: value
    }));
  }, [dispatch, handleTrackInteraction, scrollToTabSection]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ReviewsListStats, {
      className: "m-y-6",
      inline: inline,
      onUpdateFilters: onUpdateFilters,
      reviewStats: reviewStats,
      reviewType: ReviewTypes.PROFILE
    }), /*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      reviewsCount: reviewsCount,
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      catalogServices: catalogServices,
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: getDataFetchingClientRequestState(error, loading),
      children: /*#__PURE__*/_jsx(ReviewsPaginatedList, {
        onUpdateFilters: onUpdateFilters,
        ReviewRenderer: ProfileReviewRenderer,
        reviewRendererProps: {
          hasProfile
        },
        reviews: reviews,
        reviewsFetchStatus: reviewsFetchStatus,
        reviewFilters: reviewFilters,
        reviewsTotal: reviewsTotal,
        searchTerm: searchSubmitted
      })
    })]
  });
};
ReviewsCardBody.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.shape(Review)).isRequired,
  reviewStats: PropTypes.shape(ReviewStats).isRequired,
  reviewsFetchStatus: CustomPropTypes.requestState.isRequired,
  reviewsTotal: PropTypes.number.isRequired,
  reviewsCount: PropTypes.number.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  scrollToTabSection: PropTypes.func.isRequired
};
export default ReviewsCardBody;